/**
 * Address autocomplete for the site form.
 *
 * Type a few words, pick the right result, and the street, town, province, postcode and
 * the coordinates all fill at once. The coordinates are the point: they are what every
 * future clock-in on that site is judged against, and typing them by hand from a maps
 * app is how a site ends up fenced around the building next door.
 *
 * The search goes through our own API rather than straight to Amazon Location, so no AWS
 * credential lives in this bundle — see hh-backend/infra/lib/location-stack.ts.
 */

import { useEffect, useId, useRef, useState } from 'react';

import { useI18n } from '@/i18n';
import { api, type AddressSuggestion, type LatLng } from '@/lib/api';

interface Props {
  /** The free-text value, which is also the site's address line. */
  value: string;
  onChange: (next: string) => void;
  onPick: (suggestion: AddressSuggestion) => void;
  /** Bias results towards the map's current centre, when there is one. */
  near?: LatLng;
  placeholder?: string;
}

/*
 * Long enough that a pause between words does not fire a search, short enough that the
 * list is there by the time the eye moves to it. Each search is a billed request, so
 * this number has a small running cost attached to it.
 */
const DEBOUNCE_MS = 350;
const MIN_CHARS = 3;

export function AddressSearch({ value, onChange, onPick, near, placeholder }: Props) {
  const { t } = useI18n();
  const listId = useId();

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);

  const wrapper = useRef<HTMLDivElement | null>(null);

  /*
   * Set while a suggestion is being applied.
   *
   * Picking one calls onChange with the chosen address, which changes `value`, which
   * would otherwise trigger a fresh search for the text the user just accepted — a
   * billed request for a result they already have, and a dropdown that reopens under
   * their cursor.
   */
  const justPicked = useRef(false);

  useEffect(() => {
    if (justPicked.current) {
      justPicked.current = false;
      return;
    }

    const query = value.trim();
    if (query.length < MIN_CHARS) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const timer = setTimeout(() => {
      api
        .suggestAddresses(query, near)
        .then((result) => {
          // A slow response for an earlier query must not overwrite a newer one.
          if (cancelled) return;
          setSuggestions(result.suggestions);
          setOpen(result.suggestions.length > 0);
          setActive(-1);
        })
        .catch(() => {
          // Search being unavailable is not an error worth interrupting anyone over:
          // every field it fills can still be typed by hand.
          if (!cancelled) setSuggestions([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      setLoading(false);
    };
  }, [value, near]);

  // Click anywhere else and the list closes.
  useEffect(() => {
    if (!open) return;

    const onDocumentClick = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [open]);

  const choose = (suggestion: AddressSuggestion) => {
    justPicked.current = true;
    onPick(suggestion);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter' && active >= 0) {
      // Only when something is highlighted — otherwise Enter belongs to the form.
      event.preventDefault();
      choose(suggestions[active]!);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapper} className="relative">
      <input
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => setOpen(suggestions.length > 0)}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
      />

      {loading ? (
        <span className="absolute end-3 top-1/2 -translate-y-1/2 text-xs text-subtle">
          {t('common.search')}…
        </span>
      ) : null}

      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 start-0 end-0 mt-1 bg-surface border border-border rounded shadow-lg max-h-64 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <li
              key={`${s.label}-${i}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              // onMouseDown, not onClick: the input's blur fires first on a click and
              // would close the list before the click ever lands.
              onMouseDown={(e) => {
                e.preventDefault();
                choose(s);
              }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 text-sm cursor-pointer ${
                i === active ? 'bg-[var(--color-primary-soft)] text-primary' : 'hover:bg-surface-alt'
              }`}
            >
              <div className="font-medium">{s.label}</div>
              {s.lat === null ? (
                // Worth saying: this one will not move the pin.
                <div className="text-xs text-warning">{t('sites.noCoordinates')}</div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
